// frontend/src/components/UserProfile.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../config';
import './UserProfile.css'; // Импортируем CSS

// Добавляем onSendMessage в пропсы
const UserProfile = ({ userData, currentUserId, onClose, onShowInvitePath, onSendMessage, searchQuery }) => {
  
  // ... (существующие состояния profileData, loading, error, currentUserTags, searchWords) ...
  const [profileData, setProfileData] = useState(userData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserTags, setCurrentUserTags] = useState([]);
  const [searchWords, setSearchWords] = useState([]);
  const fetchAttemptCount = useRef(0);
  const prevUserIdRef = useRef(null);


  // ... (существующие useEffect и useCallback функции: highlightText, fetchMissingData, fetchCurrentUserTags, handleShowInvitePath, handleCloseClick, isCommonTag) ...
  
    // Обработка поискового запроса
    useEffect(() => {
      console.log('[UserProfile] Получен поисковый запрос:', searchQuery);
      console.log('[UserProfile] Токены в профиле:', profileData?.matching_fts_tokens);
      
      if (searchQuery) {
        const words = searchQuery.toLowerCase().split(/\s+/).filter(word => word.length >= 3);
        setSearchWords(words);
        console.log('[UserProfile] Поисковые слова из запроса:', words);
      } 
      else if (profileData?.matching_fts_tokens && profileData.matching_fts_tokens.length) {
        const profileText = [
          profileData.bio || '',
          profileData.principles || '',
          profileData.interests || '', 
          profileData.development || '',
          profileData.resources || '',
          profileData.contact_goals || ''
        ].join(' ').toLowerCase();
        const validTokens = profileData.matching_fts_tokens.filter(token => profileText.includes(token.toLowerCase()));
        setSearchWords(validTokens);
        console.log('[UserProfile] Используем отфильтрованные matching_fts_tokens:', validTokens);
      } 
      else {
        setSearchWords([]);
        console.log('[UserProfile] Сбрасываем поисковые слова');
      }
    }, [searchQuery, profileData]);

    // Условие для дозагрузки
    const needsFetching = (data) => {
      const bioMissing = data?.bio === undefined || data?.bio === null;
      const linksMissing = data?.links === undefined || data?.links === null || !Array.isArray(data.links);
      // Можно добавить проверку на другие расширенные поля, если они не всегда приходят сразу
      const principlesMissing = data?.principles === undefined || data?.principles === null;
      const interestsMissing = data?.interests === undefined || data?.interests === null;
      return bioMissing || linksMissing || principlesMissing || interestsMissing; // Добавил проверку
    };

    // Функция загрузки дополнительных данных
    const fetchMissingData = useCallback(async () => {
      if (!profileData || !profileData.telegram_id || fetchAttemptCount.current >= 3) return;
      
      console.log(`[UserProfile] Дозагрузка данных для ${profileData.telegram_id}`);
      setLoading(true);
      setError(null); // Сбрасываем ошибку перед попыткой
      fetchAttemptCount.current += 1; // Увеличиваем счетчик попыток

      try {
        const formData = new FormData();
        formData.append('telegram_id', profileData.telegram_id);
        
        const response = await fetch(getApiUrl('/api/get_user_profile'), {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка получения данных: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data && data.user) {
          // Обновляем только недостающие поля, чтобы не перезаписывать все
          setProfileData(prev => ({...prev, ...data.user})); 
          console.log(`[UserProfile] Дополнительные данные загружены для ${profileData.telegram_id}`);
        } else {
            console.warn(`[UserProfile] Ответ API не содержит user для ${profileData.telegram_id}`);
        }
      } catch (err) {
        console.error('[UserProfile] Ошибка при загрузке полных данных:', err);
        setError('Не удалось загрузить полный профиль');
      } finally {
        setLoading(false);
      }
    }, [profileData]); // Зависимость от profileData

    // Функция загрузки тегов текущего пользователя
    const fetchCurrentUserTags = useCallback(async () => {
      if (!currentUserId) return;
      // Эта функция не должна устанавливать loading/error основного профиля
      try {
        const formData = new FormData();
        formData.append('telegram_id', currentUserId);
        const response = await fetch(getApiUrl('/api/get_user_profile'), { method: 'POST', body: formData });
        if (response.ok) {
          const data = await response.json();
          if (data?.user?.tags) {
            setCurrentUserTags(data.user.tags);
          }
        }
      } catch (err) {
        console.error('[UserProfile] Ошибка при загрузке тегов текущего пользователя:', err);
      }
    }, [currentUserId]);

    // Эффект для дозагрузки данных
    useEffect(() => {
      if (profileData && needsFetching(profileData)) {
        fetchMissingData();
      }
    }, [profileData, fetchMissingData]);

    // Эффект для загрузки тегов текущего пользователя
    useEffect(() => {
      fetchCurrentUserTags();
    }, [fetchCurrentUserTags]);

    // Эффект для сброса состояния при смене пользователя
    useEffect(() => {
      if (prevUserIdRef.current !== userData?.telegram_id) {
        console.log(`[UserProfile] Смена пользователя: ${prevUserIdRef.current} -> ${userData?.telegram_id}`);
        prevUserIdRef.current = userData?.telegram_id;
        setProfileData(userData); // Устанавливаем НОВЫЕ данные
        setLoading(false); // Сбрасываем флаги загрузки/ошибки
        setError(null);
        fetchAttemptCount.current = 0; // Сбрасываем счетчик попыток загрузки
      }
    }, [userData]);

  // Обработчик клика для кнопки просмотра пути приглашений
  const handleShowInvitePath = () => {
    if (onShowInvitePath && profileData) {
      onShowInvitePath(profileData.telegram_id);
    }
  };

  // Обработчик клика для закрытия профиля
  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Проверка, является ли тег общим
  const isCommonTag = (tag) => {
    if (profileData?.telegram_id === currentUserId) {
      return false;
    }
    return currentUserTags.includes(tag);
  };

  // Функция для выделения искомых слов
  const highlightText = (text) => {
    if (!text || typeof text !== 'string' || !searchWords.length) {
      return <p>{text || ''}</p>;
    }
    try {
      const escapedWords = searchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      const pattern = new RegExp(`(${escapedWords.join('|')})`, 'gi');
      const parts = text.split(pattern);
      let result = [];
      let matchIndex = 1;
      for (let i = 0; i < parts.length; i++) {
        if (i % 2 === 0) {
          result.push(<span key={`text-${i}`}>{parts[i]}</span>);
        } else {
          result.push(<span key={`match-${matchIndex++}`} className="highlighted-word">{parts[i]}</span>);
        }
      }
      return <p>{result}</p>;
    } catch (e) {
      console.error('Ошибка при подсветке текста:', e);
      return <p>{text}</p>;
    }
  };


  if (!profileData) {
    return (
      <div className="mobile-screen">
        <div className="screen-content profile-view">
           {/* Добавляем кнопку закрытия и здесь */}
           <div className="profile-header">
             <button className="close-button" onClick={handleCloseClick}>x</button>
             <h1>Профиль пользователя</h1>
           </div>
           <div className="card profile-card">
             <p>Информация о пользователе недоступна</p>
             {error && <div className="error-message">{error}</div>}
           </div>
        </div>
      </div>
    );
  }

  let avatarSrc = '/avatars/default-avatar.png';
  if (profileData.avatar) {
    if (profileData.avatar.startsWith('/')) {
      avatarSrc = profileData.avatar;
    } else if (profileData.avatar.startsWith('avatars/')) {
      avatarSrc = '/' + profileData.avatar;
    }
  }

  const links = [];
  if (profileData.links && Array.isArray(profileData.links)) {
    profileData.links.forEach(link => { if (link && link.trim() !== '') links.push(link); });
  } else {
    if (profileData.website) links.push(profileData.website);
    if (profileData.instagram) links.push(profileData.instagram);
    // ... и т.д. для обратной совместимости
  }

  const formatLink = (value) => {
    if (!value) return null;
    const hasProtocol = value.startsWith('http://') || value.startsWith('https://');
    const url = hasProtocol ? value : `https://${value}`;
    let displayValue = value.replace(/^https?:\/\//, ''); // Убираем http(s)://
    if (displayValue.length > 30) { // Обрезаем слишком длинные ссылки
        displayValue = displayValue.substring(0, 27) + '...';
    }
    return { icon: '🔗', text: displayValue, url: url };
  };

  // Определяем, нужно ли показывать кнопки действий
  const isOwnProfile = profileData.telegram_id === currentUserId;
  const showInvitePathButton = !isOwnProfile && onShowInvitePath;
  const showSendMessageButton = !isOwnProfile && onSendMessage; // <-- НОВАЯ ПЕРЕМЕННАЯ

  return (
    <div className="mobile-screen">
      <div className="screen-content profile-view">
        <div className="profile-header">
          <button className="close-button" onClick={handleCloseClick}>↩</button>
          <h1>Профиль пользователя</h1>
        </div>

        {loading && <div className="loading-indicator">Загрузка данных...</div>}
        {error && <div className="error-message">{error}</div>}

        {searchWords.length > 0 && (
          <div className="search-highlight-info">
            <p>Подсвеченные слова показывают совпадения с поисковым запросом</p>
          </div>
        )}

        <div className="card profile-card">
          <div className="profile-avatar">
            <img 
              src={avatarSrc}
              alt={profileData.name || profileData.username}
              onError={(e) => {e.target.onerror = null; e.target.src = '/avatars/default-avatar.png';}}
            />
          </div>
          
          <h2 className="profile-name">{profileData.name || profileData.username}</h2>
          
          {isOwnProfile && (
            <div className="profile-badge current">Это вы</div>
          )}

          {/* --- Блок кнопок действий --- */}
          <div className="profile-actions-container">
              {showInvitePathButton && (
                <button
                  className="button button-primary invite-path-button" // Оставляем эти классы
                  onClick={handleShowInvitePath}
                  // Если вы хотите светло-бежевый цвет, добавьте инлайн стиль сюда:
                  // style={{ backgroundColor: '#FAF0E6', color: '#5D4037' }}
                >
                  {/* Заменяем <span>🔗</span> на SVG: */}
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor" // Цвет иконки будет как у текста кнопки
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: '8px' }} // Добавляем отступ справа от иконки
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  Посмотреть путь приглашений {/* Текст кнопки */}
                </button>
              )}
    
              {/* --- НОВАЯ КНОПКА --- */}
              {showSendMessageButton && (
                 <button 
                   className="button button-primary send-message-profile-button" // Новый класс для возможной стилизации
                   onClick={() => onSendMessage(profileData)} // Вызываем onSendMessage с данными текущего профиля
                 >
                   <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                   </svg>
                   Написать сообщение
                 </button>
              )}
          </div>
          {/* --- Конец блока кнопок --- */}

        </div>

        {/* --- Остальные секции профиля --- */}
        <div className="profile-section">
          {profileData.city && (
            <div className="card location-card">
              <h3>Город</h3>
              <p>📍 {profileData.city}</p>
            </div>
          )}
          
          {/* Расширенные поля */}
          {profileData.bio && <div className="card bio-card"><h3>О пользователе</h3>{highlightText(profileData.bio)}</div>}
          {profileData.principles && <div className="card principles-card"><h3>Принципы</h3>{highlightText(profileData.principles)}</div>}
          {profileData.interests && <div className="card interests-card"><h3>Интересы</h3>{highlightText(profileData.interests)}</div>}
          {profileData.development && <div className="card development-card"><h3>Развитие</h3>{highlightText(profileData.development)}</div>}
          {profileData.resources && <div className="card resources-card"><h3>Ресурсы</h3>{highlightText(profileData.resources)}</div>}
          {profileData.contact_goals && <div className="card contact-goals-card"><h3>Цели контактов</h3>{highlightText(profileData.contact_goals)}</div>}
          
          {/* Теги (бывшие категории) */}
          {profileData.categories && profileData.categories.length > 0 && (
            <div className="card tags-card">
              <h3>Категории</h3>
              <div className="tag-list">
                {profileData.categories.map((category, index) => (
                  <span 
                    key={index} 
                    className={`tag-chip ${isCommonTag(category) ? 'common-tag' : ''}`}
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {links.length > 0 && (
            <div className="card links-card">
              <h3>Ссылки</h3>
              <ul className="links-list">
                {links.map((link, index) => {
                  const formattedLink = formatLink(link);
                  if (!formattedLink) return null; // Пропускаем пустые ссылки
                  return (
                    <li key={index}>
                      <a 
                        href={formattedLink.url} 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formattedLink.icon} {formattedLink.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        {/* --- Конец секций профиля --- */}

      </div>
    </div>
  );
};

export default UserProfile;